import { useCallback, useEffect, useRef, useState } from 'react';
import { getMyTimesheet } from '../../../api/hub/effort';

/**
 * Loads the days next to a move target so an overlap can be checked against entries the
 * page has not loaded.
 *
 * Results are cached per window for the page. The cache is dropped by `invalidate` (the
 * page calls it after every write and every refetch, so a move onto an entry that was just
 * moved sees the new position) and whenever the range or the viewed person changes.
 *
 * `checking` is true while a fetch is in flight, for the "Checking…" state. A failed fetch,
 * or a response without an entries list, rejects and is not cached: the caller refuses the
 * move rather than guess.
 *
 * @param {{memberId: string|null, from: string, to: string}} scope The page's range.
 * @returns {{
 *   load: (window: {from: string, to: string}) => Promise<object[]>,
 *   invalidate: () => void,
 *   checking: boolean,
 * }}
 */
export default function useNeighbourEntries({ memberId, from, to }) {
  const cache = useRef(new Map());
  const [checking, setChecking] = useState(false);

  const invalidate = useCallback(() => {
    cache.current.clear();
  }, []);

  useEffect(() => {
    invalidate();
  }, [memberId, from, to, invalidate]);

  const load = useCallback(async (win) => {
    const key = `${memberId ?? 'me'}|${win.from}|${win.to}`;
    const cached = cache.current.get(key);
    if (cached) return cached;

    const request = getMyTimesheet(win.from, win.to, memberId)
      .then((r) => {
        // A payload without an entries list is not an empty day: reading it as one would
        // let a move through unchecked.
        if (!Array.isArray(r?.data?.entries)) throw new Error('timesheet: unexpected response');
        return r.data.entries;
      });
    cache.current.set(key, request);
    request.catch(() => {
      if (cache.current.get(key) === request) cache.current.delete(key);
    });

    setChecking(true);
    try {
      return await request;
    } finally {
      setChecking(false);
    }
  }, [memberId]);

  return { load, invalidate, checking };
}
